import React, { useState, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, PlugZap, CheckCircle2, XCircle, MinusCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useResourceList } from "@/hooks/useResource";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { IdCell } from "@/components/kit/IdCell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, SelectField, Field } from "@/components/kit/fields";
import { MultiSelect } from "@/components/kit/MultiSelect";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate, fmtDateTime } from "@/lib/format";

const SUB_SOURCES = Array.from({ length: 9 }, (_, i) => `sub_id_${i + 1}`);
const META_EVENT_OPTS = [
  { value: "none", label: "Don't send" },
  { value: "Lead", label: "Lead" },
  { value: "CompleteRegistration", label: "CompleteRegistration" },
  { value: "Purchase", label: "Purchase" },
];
const INTERNAL_EVENTS = [
  { key: "lead", label: "Lead" },
  { key: "sale", label: "Sale / Deposit" },
  { key: "redep", label: "Redep" },
  { key: "revshare", label: "RevShare" },
];

function ActiveBadge({ status, testId }) {
  const active = status === "active";
  const cls = active ? "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30" : "bg-[#EAB308]/15 text-[#EAB308] border-[#EAB308]/30";
  return <span data-testid={testId} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}>{active ? "Active" : "Inactive"}</span>;
}

function ResultBadge({ result }) {
  const map = {
    sent: ["bg-[#22C55E]/15 text-[#22C55E]", CheckCircle2, "Sent"],
    failed: ["bg-[#EF4444]/15 text-[#EF4444]", XCircle, "Failed"],
    skipped: ["bg-[#F59E0B]/15 text-[#F59E0B]", MinusCircle, "Skipped"],
  };
  const [cls, Icon, label] = map[result] || map.skipped;
  return <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}><Icon className="h-3 w-3" />{label}</span>;
}

const Flag = ({ ok }) => ok
  ? <CheckCircle2 className="h-3.5 w-3.5 text-[#22C55E]" />
  : <XCircle className="h-3.5 w-3.5 text-muted-foreground/50" />;

function LogsDateFilter({ values, set }) {
  return (
    <div className="w-full sm:w-[190px]">
      <DateRangePicker from={values.dateFrom} to={values.dateTo}
        onChange={({ from, to }) => { set("dateFrom", from); set("dateTo", to); }} placeholder="Date range" testId="meta-logs-date-range" />
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-foreground">{children}</span>
    </div>
  );
}

function MetaLogDetailDialog({ log, onClose }) {
  if (!log) return null;
  let pretty = "";
  const raw = log.meta_response || "";
  if (raw) {
    try { pretty = JSON.stringify(JSON.parse(raw), null, 2); } catch { pretty = raw; }
  }
  const isTest = log.internal_event === "test";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/[0.1] bg-[#141417]" data-testid="meta-log-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Meta event details {log.test && <span className="rounded bg-[#A78BFA]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#A78BFA]">TEST</span>}</DialogTitle>
          <DialogDescription>{fmtDateTime(log.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <DetailRow label="Pixel ID"><span className="font-mono text-xs">{log.pixel_id || "—"}</span></DetailRow>
          <DetailRow label="Integration">{log.integration_name || "—"}</DetailRow>
          <DetailRow label="Stream">{log.stream_id ? `${log.stream_seq != null ? `#${log.stream_seq} ` : ""}${log.stream_name || ""}`.trim() : "—"}</DetailRow>
          <DetailRow label="Event">
            <span className="text-xs uppercase text-muted-foreground">{log.internal_event}</span>
            {log.meta_event && <span className="ml-1.5 rounded-md bg-[#3B82F6]/15 px-2 py-0.5 text-xs font-medium text-[#60A5FA]">{log.meta_event}</span>}
          </DetailRow>
          <DetailRow label="Event ID"><span className="break-all font-mono text-xs">{log.event_id || "—"}</span></DetailRow>
          <DetailRow label="FBC source">
            {isTest ? "—" : log.fbc_source
              ? <span className="font-mono text-xs text-[#A78BFA]">{log.fbc_source}</span>
              : "—"}
          </DetailRow>
          {!isTest && log.fbc_source && (
            <p className="text-right text-[11px] leading-snug text-muted-foreground">
              fbclid is captured automatically from the click URL into {log.fbc_source}, or taken from the value passed in that Sub ID.
            </p>
          )}
          <DetailRow label="FBC sent">
            {log.fbc_sent ? <span className="break-all font-mono text-[11px]">{log.fbc_sent}</span> : <span className="text-muted-foreground">{log.fbc_found ? "—" : "not found"}</span>}
          </DetailRow>
          <DetailRow label="Status">
            <span className="inline-flex items-center gap-2">
              <ResultBadge result={log.result} />
              {log.http_status ? <span className="font-mono text-xs text-muted-foreground">HTTP {log.http_status}</span> : null}
              {log.attempts > 1 ? <span className="text-xs text-muted-foreground">attempt {log.attempts}</span> : null}
            </span>
          </DetailRow>
          {!!log.error && (
            <div className="rounded-lg border border-[#EF4444]/25 bg-[#EF4444]/[0.07] px-3 py-2 text-sm text-[#EF4444]" data-testid="meta-log-detail-error">{log.error}</div>
          )}
          {!!pretty && (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Meta response</p>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3 font-mono text-[11px] leading-relaxed text-foreground/90" data-testid="meta-log-detail-json">{pretty}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetaLogs({ readOnly, isSuper }) {
  const [logDetail, setLogDetail] = useState(null);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["meta-capi-logs"],
    queryFn: async () => (await http.get("/meta-capi/logs")).data,
    refetchInterval: 30000,
  });
  const rows = useMemo(() => data?.items || [], [data]);
  const filterFn = (l, f) => {
    if (f.dateFrom && (l.created_at || "") < f.dateFrom) return false;
    if (f.dateTo && (l.created_at || "") > f.dateTo + "T23:59:59") return false;
    return true;
  };
  const integOpts = useMemo(() => {
    const seen = {};
    rows.forEach((r) => { if (r.integration_id) seen[r.integration_id] = r.integration_name || r.integration_id; });
    return Object.entries(seen).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const columns = [
    { key: "created_at", label: "Date", sortable: true, sortValue: (r) => r.created_at,
      render: (r) => <span className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</span> },
    { key: "integration_name", label: "Integration", render: (r) => (
      <span className="text-sm text-foreground">{r.integration_name || "—"}{r.test && <span className="ml-1.5 rounded bg-[#A78BFA]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#A78BFA]">TEST</span>}</span>
    ) },
    { key: "stream", label: "Stream", render: (r) => r.stream_id
      ? <span className="text-xs text-foreground/90">{r.stream_seq != null ? `#${r.stream_seq} ` : ""}{r.stream_name || "—"}</span>
      : <span className="text-xs text-muted-foreground">—</span> },
    { key: "order_id", label: "Conversion", mono: true, render: (r) => r.order_id
      ? <span className="font-mono text-xs text-foreground/90">{r.order_id}</span>
      : <span className="text-xs text-muted-foreground">—</span> },
    { key: "internal_event", label: "Event", render: (r) => <span className="text-xs uppercase text-muted-foreground">{r.internal_event || "—"}</span> },
    { key: "meta_event", label: "Meta Event", render: (r) => r.meta_event
      ? <span className="rounded-md bg-[#3B82F6]/15 px-2 py-0.5 text-xs font-medium text-[#60A5FA]">{r.meta_event}</span>
      : <span className="text-xs text-muted-foreground">—</span> },
    { key: "pixel_id", label: "Pixel", mono: true, render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.pixel_id || "—"}</span> },
    { key: "event_id", label: "Event ID", render: (r) => r.event_id ? <IdCell value={r.event_id} testId="meta-event-id" maxWidth={140} /> : "—" },
    { key: "fbc_found", label: "FBC", align: "center", render: (r) => r.internal_event === "test" ? <span className="text-xs text-muted-foreground">—</span> : <Flag ok={r.fbc_found} /> },
    { key: "ip_found", label: "IP", align: "center", render: (r) => <Flag ok={r.ip_found} /> },
    { key: "ua_found", label: "UA", align: "center", render: (r) => <Flag ok={r.ua_found} /> },
    { key: "result", label: "Result", sortable: true, sortValue: (r) => r.result, render: (r) => <ResultBadge result={r.result} /> },
    { key: "http_status", label: "HTTP", mono: true, render: (r) => <span className="font-mono text-xs">{r.http_status || "—"}</span> },
    { key: "attempts", label: "Attempt", mono: true, align: "center", render: (r) => <span className="text-xs text-muted-foreground">{r.attempts || "—"}</span> },
    { key: "detail", label: "Response / Error", render: (r) => {
      const txt = r.error || r.meta_response || "";
      return txt ? (
        <button type="button" data-testid={`meta-log-detail-open-${r.id}`}
          onClick={(e) => { e.stopPropagation(); setLogDetail(r); }}
          className="max-w-[280px] truncate text-left text-xs text-foreground/80 underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground">
          {txt}
        </button>
      ) : <span className="text-xs text-muted-foreground">—</span>;
    } },
  ];

  return (
    <>
    <DataTablePro testId="meta-logs-table" columns={columns} rows={rows} loading={isLoading}
      searchKeys={["integration_name", "stream_name", "order_id", "event_id", "meta_event", "error"]}
      searchPlaceholder="Search by integration, stream, order, event ID..." exportName="meta-capi-logs"
      filterFn={filterFn}
      filters={[
        { key: "result", label: "Result", getValue: (r) => r.result,
          options: [{ value: "sent", label: "Sent" }, { value: "failed", label: "Failed" }, { value: "skipped", label: "Skipped" }] },
        ...(integOpts.length > 1 ? [{ key: "integration_id", label: "Integration", getValue: (r) => r.integration_id, options: integOpts }] : []),
      ]}
      customFilters={LogsDateFilter}
      toolbarRight={
        <Button variant="outline" onClick={() => refetch()} data-testid="meta-logs-refresh"
          className="h-9 gap-1.5 border-white/[0.12] bg-transparent">
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      }
      emptyText="No Meta events yet. Logs appear when conversions on the selected streams fire an integration." />
    <MetaLogDetailDialog log={logDetail} onClose={() => setLogDetail(null)} />
    </>
  );
}

export default function MetaIntegrations() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuper = (user.permissions || []).includes("*");
  const { data, isLoading } = useQuery({
    queryKey: ["meta-integrations"],
    queryFn: async () => (await http.get("/meta-capi")).data,
  });
  const readOnly = isSuper || !!data?.read_only;
  const rows = data?.items || [];
  const { data: streamsData } = useResourceList("streams", {});
  const streams = useMemo(() => streamsData?.items || [], [streamsData]);
  const streamOptions = useMemo(() => streams
    .filter((s) => s.buyer_id === user.id)
    .map((s) => ({
      value: s.id,
      label: `#${s.tenant_seq ?? ""} ${s.name || s.stream_key}${(s.status || "active") !== "active" ? " (paused)" : ""}`.trim(),
      search: `#${s.tenant_seq ?? ""} ${s.tenant_seq ?? ""} ${s.name || ""} ${s.stream_key || ""} ${s.offer_name || ""}`,
    })), [streams, user.id]);

  const empty = { name: "", pixel_id: "", access_token: "", status: "active", fbc_source: "sub_id_1",
    stream_ids: [], events: { lead: "none", sale: "none", redep: "none", revshare: "none" }, test_event_code: "" };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [del, setDel] = useState(null);
  const baseline = useRef(JSON.stringify(empty));
  const dirty = JSON.stringify(form) !== baseline.current;

  const openNew = () => {
    setEditing(null); setForm(empty); setTestResult(null);
    baseline.current = JSON.stringify(empty); setOpen(true);
  };
  const openEdit = (row) => {
    if (readOnly) return;
    const init = { name: row.name || "", pixel_id: row.pixel_id || "", access_token: "",
      status: row.status || "active", fbc_source: row.fbc_source || "sub_id_1",
      stream_ids: row.stream_ids || [], events: { lead: "none", sale: "none", redep: "none", revshare: "none", ...(row.events || {}) },
      test_event_code: row.test_event_code || "" };
    setEditing(row); setForm(init); setTestResult(null);
    baseline.current = JSON.stringify(init); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!/^\d{5,20}$/.test(form.pixel_id.trim())) return toast.error("Pixel ID must be a numeric Meta Pixel ID");
    if (!editing && !form.access_token.trim()) return toast.error("Access Token is required");
    setSaving(true);
    try {
      if (editing) await http.put(`/meta-capi/${editing.id}`, form);
      else await http.post("/meta-capi", form);
      toast.success(editing ? "Integration updated" : "Integration created");
      qc.invalidateQueries({ queryKey: ["meta-integrations"] });
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await http.delete(`/meta-capi/${id}`);
      toast.success("Integration deleted");
      qc.invalidateQueries({ queryKey: ["meta-integrations"] });
      qc.invalidateQueries({ queryKey: ["meta-capi-logs"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const testConnection = async () => {
    if (!/^\d{5,20}$/.test(form.pixel_id.trim())) return toast.error("Enter a numeric Pixel ID first");
    if (!editing && !form.access_token.trim()) return toast.error("Enter the Access Token first");
    setTesting(true); setTestResult(null);
    try {
      const body = { pixel_id: form.pixel_id.trim(), test_event_code: form.test_event_code.trim() };
      if (form.access_token.trim()) body.access_token = form.access_token.trim();
      if (editing) body.integration_id = editing.id;
      const { data: res } = await http.post("/meta-capi/test", body);
      setTestResult(res);
      qc.invalidateQueries({ queryKey: ["meta-capi-logs"] });
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || "Test request failed" });
    } finally { setTesting(false); }
  };

  const eventsSummary = (r) => INTERNAL_EVENTS
    .map(({ key, label }) => ({ label, v: (r.events || {})[key] || "none" }))
    .filter((x) => x.v !== "none");

  const columns = [
    { key: "tenant_seq", label: "ID", mono: true, sortable: true, sortValue: (r) => Number(r.tenant_seq || 0),
      render: (r) => <span className="font-mono text-xs text-muted-foreground">#{r.tenant_seq}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    ...(isSuper ? [{ key: "owner", label: "Owner", render: (r) => (
      <div className="min-w-0">
        <div className="truncate text-sm text-foreground">{r.owner_seq != null ? `#${r.owner_seq} ` : ""}{r.owner_name || "—"}</div>
        {r.owner_email && <div className="truncate text-[11px] text-muted-foreground">{r.owner_email}</div>}
      </div>
    ) }] : []),
    { key: "pixel_id", label: "Pixel ID", mono: true, render: (r) => <span className="font-mono text-xs text-foreground/90">{r.pixel_id}</span> },
    { key: "token", label: "Token", mono: true, render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.token_mask}</span> },
    { key: "fbc_source", label: "FBC source", mono: true, render: (r) => <span className="rounded-md bg-white/[0.05] px-2 py-0.5 font-mono text-xs text-[#A78BFA]">{r.fbc_source}</span> },
    { key: "streams_count", label: "Streams", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.streams_count || 0),
      render: (r) => <span data-testid={`meta-streams-count-${r.id}`} className="text-sm">{r.streams_count}</span> },
    { key: "events", label: "Events", render: (r) => {
      const list = eventsSummary(r);
      return list.length
        ? <div className="flex flex-wrap gap-1">{list.map((x) => <span key={x.label} className="rounded bg-[#3B82F6]/10 px-1.5 py-0.5 text-[11px] text-[#60A5FA]">{x.label} → {x.v}</span>)}</div>
        : <span className="text-xs text-muted-foreground">Nothing mapped</span>;
    } },
    { key: "status", label: "Status", sortable: true, sortValue: (r) => r.status, render: (r) => <ActiveBadge status={r.status} testId={`meta-status-${r.id}`} /> },
    { key: "created_at", label: "Created", sortable: true, sortValue: (r) => r.created_at, render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
    ...(!readOnly ? [{ key: "_actions", label: "Actions", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <button type="button" title="Edit" data-testid={`edit-meta-${r.id}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r); }}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"><Pencil className="h-4 w-4" /></button>
        <button type="button" title="Delete" data-testid={`delete-meta-${r.id}`}
          onClick={(e) => { e.stopPropagation(); setDel(r); }}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[#EF4444]/10 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
      </div>
    ) }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Facebook" subtitle="Send conversion events from your streams to Meta via the Conversions API." />
      <Tabs defaultValue="integrations">
        <TabsList className="bg-white/[0.04]">
          <TabsTrigger value="integrations" data-testid="meta-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="logs" data-testid="meta-tab-logs">Send Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="integrations" className="mt-4">
          <DataTablePro testId="meta-integrations-table" columns={columns} rows={rows} loading={isLoading}
            searchKeys={["name", "pixel_id", "fbc_source", "owner_name"]} searchPlaceholder="Search by name or pixel..."
            exportName="meta-integrations"
            filters={[{ key: "status", label: "Status", getValue: (r) => r.status,
              options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] }]}
            onRowClick={!readOnly ? (r) => openEdit(r) : undefined}
            emptyText="No Meta integrations yet. Add one to forward your conversions to Facebook."
            toolbarRight={!readOnly && (
              <Button onClick={openNew} data-testid="add-meta-integration-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add Integration</Button>
            )} />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <MetaLogs readOnly={readOnly} isSuper={isSuper} />
        </TabsContent>
      </Tabs>

      <FormDrawer open={open} onOpenChange={setOpen} title={editing ? "Edit Meta Integration" : "New Meta Integration"}
        subtitle="Server-side events to Facebook (Conversions API)" size="md" dirty={dirty}
        onSave={save} saving={saving} saveLabel={editing ? "Save changes" : "Create"} testId="meta-drawer">
        <div className="space-y-5">
          <TextField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="meta-name" placeholder="e.g. FB main account" />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Pixel ID" value={form.pixel_id} onChange={(v) => setForm((f) => ({ ...f, pixel_id: v }))} testId="meta-pixel-id" placeholder="1234567890123456" />
            <SelectField label="Status" value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))}
              options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} testId="meta-status-select" />
          </div>
          <TextField label="Access Token" type="password" autoComplete="new-password"
            value={form.access_token} onChange={(v) => setForm((f) => ({ ...f, access_token: v }))} testId="meta-access-token"
            placeholder={editing ? `${editing.token_mask} — enter a new token to replace` : "EAAB..."}
            hint={editing ? "The saved token is hidden. Leave empty to keep it, or paste a new one to replace it." : "Generated in Meta Events Manager → Settings → Conversions API."} />
          <Field label="Facebook Click ID source"
            hint={`The click parameter that carries the fbclid. Example: .../api/r/KEY?${form.fbc_source}={fbclid} → choose ${form.fbc_source}.`}>
            <SelectField value={form.fbc_source} onChange={(v) => setForm((f) => ({ ...f, fbc_source: v }))}
              options={SUB_SOURCES.map((s) => ({ value: s, label: s }))} testId="meta-fbc-source" />
          </Field>
          <Field label="Streams" hint="Events fire only for conversions on your selected streams. A deleted stream is detached automatically; if none remain the integration goes Inactive.">
            <MultiSelect values={form.stream_ids} onChange={(v) => setForm((f) => ({ ...f, stream_ids: v }))}
              options={streamOptions} label="Select your streams" searchPlaceholder="Search by #ID, name, key..." testId="meta-streams" widthClass="w-[360px]" />
          </Field>
          <div className="space-y-2.5 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">Event mapping</p>
            {INTERNAL_EVENTS.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-2 items-center gap-4">
                <span className="text-sm text-foreground/90">{label}</span>
                <SelectField value={form.events[key] || "none"}
                  onChange={(v) => setForm((f) => ({ ...f, events: { ...f.events, [key]: v } }))}
                  options={META_EVENT_OPTS} testId={`meta-event-${key}`} />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Purchase automatically uses the conversion&apos;s Payout amount and currency as the event value.</p>
          </div>
          <TextField label="Test Event Code (optional)" value={form.test_event_code}
            onChange={(v) => setForm((f) => ({ ...f, test_event_code: v }))} testId="meta-test-code"
            placeholder="TEST12345" hint="Used ONLY by Test Connection — never sent with production events. Get it in Events Manager → Test Events." />
          <div className="space-y-2">
            <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="meta-test-connection-btn"
              className="h-9 gap-1.5 border-white/[0.15] bg-transparent">
              <PlugZap className={`h-4 w-4 ${testing ? "animate-pulse" : ""}`} /> {testing ? "Testing…" : "Test Connection"}
            </Button>
            {testResult && (
              <div data-testid="meta-test-result"
                className={`rounded-lg border px-3 py-2.5 text-sm ${testResult.ok ? "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]" : "border-[#EF4444]/30 bg-[#EF4444]/10 text-[#EF4444]"}`}>
                {testResult.ok ? "✓ " : "✗ "}{testResult.message}
                {testResult.http_status ? <span className="ml-1.5 opacity-70">(HTTP {testResult.http_status})</span> : null}
              </div>
            )}
          </div>
        </div>
      </FormDrawer>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="meta-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meta integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-medium text-foreground">{del?.name}</span> and its send logs. Your streams and conversions are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="meta-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="meta-delete-confirm" onClick={() => { remove(del.id); setDel(null); }}
              className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
